import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery } from '@tanstack/react-query';
import { LiveScreen, Select, StatusPill, formatDate, formatDateTime, formatNumber } from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { FSC_CLAIMS, PLOT_TYPES, optionLabel } from '@/lib/practiceLists';
import { callRpc, describeError } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { LABELS_URL, SATELLITE_ATTRIBUTION, SATELLITE_URL, TILE_ATTRIBUTION, TILE_URL } from '@/screens/parts/PlotMap';

interface Props {
  plot_code: string | null; plot_name: string | null; plot_no?: string | null; farmer_id?: string | null;
  farmer_name: string | null; village_name: string | null;
  area_ha: number | null; plot_status: string | null; risk_status: string | null; is_draft: boolean;
  open_alert: boolean; monitoring_status: string | null;
}
interface Collection { type: 'FeatureCollection'; truncated: boolean; features: Array<GeoJSON.Feature<GeoJSON.Geometry, Props>> }
interface HouseProps { farmer_code: string | null; farmer_name: string | null; village_name: string | null; accuracy_m: number | null }
interface HouseCollection { type: 'FeatureCollection'; truncated: boolean; features: Array<GeoJSON.Feature<GeoJSON.Point, HouseProps>> }
interface EarthFarmer { id: string; name: string; code: string | null; plots: string | null; bbox: [number, number, number, number] | null }
/** A farmer's house, captured by GPS at the door. */
const HOUSE_COLOUR = '#2563eb';
interface Extent { plot_count: number; mapped_count: number; bbox: [number, number, number, number] | null }
interface Summary {
  plot: Record<string, unknown> & { id: string; plot_code: string | null; plot_name: string | null; plot_status: string | null; risk_status: string | null;
    declared_area_ha: number | null; mapped_area_ha: number | null; area_variance_pct: number | null; plot_type: string | null; fsc_claim: string | null;
    is_draft: boolean; conservation_feature_count: number; hazard_feature_count: number; has_indigenous_neighbour: boolean };
  farmer: { code: string | null; name: string | null; plot_count: number | null };
  place: { village: string | null; block: string | null; district: string | null; state: string | null };
  trees: { standing: number; species: Array<{ species: string; standing: number | null }> } | null;
  monitoring: { status: string; provider: string | null; baseline_at: string | null; last_checked_at: string | null;
    next_due_at: string | null; observations: number; open_changes: number } | null;
  alerts: { open: number; total: number; latest: string | null } | null;
}

/** One colour per state, in the order a reader should notice them. */
const COLOURS = {
  alert: '#dc2626',       // an unacknowledged satellite alert
  monitored: '#22c55e',   // monitoring active
  pending: '#f59e0b',     // waiting for its baseline
  draft: '#94a3b8',       // not finished
  other: '#facc15',       // mapped, not monitored
};

function colourOf(p: Props): string {
  if (p.open_alert) return COLOURS.alert;
  if (p.is_draft) return COLOURS.draft;
  if (p.monitoring_status === 'active') return COLOURS.monitored;
  if (p.monitoring_status === 'pending_baseline') return COLOURS.pending;
  return COLOURS.other;
}

/** Simplify more the further out the reader is: a 2 ha plot needs no vertex detail at district scale. */
function toleranceFor(zoom: number): number {
  if (zoom >= 15) return 0;
  if (zoom >= 13) return 0.00005;
  if (zoom >= 10) return 0.0003;
  return 0.002;
}

const INDIA: L.LatLngBoundsExpression = [[6.5, 68], [35.5, 97.5]];

/**
 * Earth View, first called Plot map (owner, 2026-09-27: "Each org shall have a satellite view of all polygons, may not edit
 * required. on click of polygon give summary of the polygon"). Every boundary the reader can see, on
 * imagery, read-only. Loaded by view as the map moves - an organisation of 40-50k plots cannot be
 * sent whole - and coloured by what needs attention. The data comes from api.fn_plot_map and
 * api.fn_plot_map_summary, both security invoker, so this shows exactly what the Plots screen shows.
 */
export default function PlotMapScreen() {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.GeoJSON | null>(null);
  const houses = useRef<L.LayerGroup | null>(null);
  // What to show (owner, 2026-09-27: "dropdown for selection of farmer ... check box: to display
  // farmer address GPS, Farmer Plots. Default display all"). Read through refs by the map's
  // move handler, which is bound once.
  const [farmerId, setFarmerId] = useState('');
  const [showPlots, setShowPlots] = useState(true);
  const [showHouses, setShowHouses] = useState(true);
  const filters = useRef({ farmerId: '', showPlots: true, showHouses: true });
  filters.current = { farmerId, showPlots, showHouses };
  const [housesShown, setHousesShown] = useState(0);
  const farmers = useQuery({
    queryKey: ['earth-view-farmers'],
    queryFn: () => callRpc<EarthFarmer[]>('fn_earth_view_farmers', { p_search: null, p_limit: 2000 }),
    enabled: isSupabaseConfigured,
  });
  const request = useRef(0);
  const [extent, setExtent] = useState<Extent | null>(null);
  const [shown, setShown] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);

  async function loadView() {
    const instance = map.current;
    if (!instance || !isSupabaseConfigured) return;
    const b = instance.getBounds();
    const box = { p_west: b.getWest(), p_south: b.getSouth(), p_east: b.getEast(), p_north: b.getNorth() };
    const { farmerId: only, showPlots: plotsOn, showHouses: housesOn } = filters.current;
    const mine = ++request.current;
    try {
      const [plotData, houseData] = await Promise.all([
        plotsOn
          ? callRpc<Collection>('fn_plot_map', { ...box, p_tolerance: toleranceFor(instance.getZoom()), p_farmer_id: only || null })
          : Promise.resolve(null),
        housesOn
          ? callRpc<HouseCollection>('fn_farmer_house_map', { ...box, p_farmer_id: only || null })
          : Promise.resolve(null),
      ]);
      if (mine !== request.current) return;           // a newer view already asked
      // Plots as their polygons only (owner, 2026-09-27: "only polygon").
      layer.current?.clearLayers();
      if (plotData) layer.current?.addData(plotData as unknown as GeoJSON.GeoJsonObject);
      houses.current?.clearLayers();
      for (const house of houseData?.features ?? []) {
        const [lng, lat] = house.geometry.coordinates as [number, number];
        const p = house.properties;
        L.circleMarker([lat, lng], { radius: 6, color: '#ffffff', weight: 2, fillColor: HOUSE_COLOUR, fillOpacity: 1 })
          .bindTooltip(`House - ${p.farmer_name ?? ''}${p.farmer_code ? ` (${p.farmer_code})` : ''}${p.village_name ? `, ${p.village_name}` : ''}${p.accuracy_m ? ` · GPS ±${Math.round(p.accuracy_m)} m` : ''}`)
          .addTo(houses.current as L.LayerGroup);
      }
      setShown(plotData?.features.length ?? 0);
      setHousesShown(houseData?.features.length ?? 0);
      setTruncated(Boolean(plotData?.truncated || houseData?.truncated));
      setError(null);
    } catch (e) {
      setError(describeError(e as { message?: string; code?: string }));
    }
  }

  // A change of farmer or of what is shown redraws the view; a farmer chosen flies there.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const chosen = (farmers.data ?? []).find((row) => row.id === farmerId);
    // Redraw now for the new filter; the fly-to then redraws again for the new view. Leaving the
    // first redraw to the move event missed the case where the farmer is already in view.
    void loadView();
    if (chosen?.bbox) {
      instance.fitBounds([[chosen.bbox[1], chosen.bbox[0]], [chosen.bbox[3], chosen.bbox[2]]], { padding: [40, 40], maxZoom: 17 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmerId, showPlots, showHouses]);

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, { zoomControl: true });
    const satellite = L.layerGroup([
      L.tileLayer(SATELLITE_URL, { attribution: SATELLITE_ATTRIBUTION, maxNativeZoom: 18, maxZoom: 20 }),
      L.tileLayer(LABELS_URL, { maxNativeZoom: 18, maxZoom: 20, opacity: 0.9 }),
    ]);
    const street = L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 });
    satellite.addTo(instance);
    L.control.layers({ Satellite: satellite, Map: street }, undefined, { position: 'topright' }).addTo(instance);

    layer.current = L.geoJSON(undefined, {
      style: (feature) => {
        const colour = colourOf(feature?.properties as Props);
        return { color: colour, weight: 2, fillColor: colour, fillOpacity: 0.18,
                 dashArray: (feature?.properties as Props)?.is_draft ? '4 4' : undefined };
      },
      onEachFeature: (feature, shape) => {
        const p = feature.properties as Props;
        shape.bindTooltip(`${p.plot_code ?? ''}${p.farmer_name ? ` - ${p.farmer_name}` : ''}`, { sticky: true });
        shape.on('click', () => setOpenId(String(feature.id)));
      },
    }).addTo(instance);
    houses.current = L.layerGroup().addTo(instance);

    map.current = instance;
    instance.fitBounds(INDIA);
    let timer: ReturnType<typeof setTimeout> | undefined;
    instance.on('moveend', () => { clearTimeout(timer); timer = setTimeout(() => void loadView(), 250); });
    setTimeout(() => instance.invalidateSize(), 150);

    if (isSupabaseConfigured) {
      void callRpc<Extent>('fn_plot_map_extent').then((e) => {
        // The map may have been taken down before the answer came (a remount): leave it alone.
        if (map.current !== instance) return;
        setExtent(e);
        if (e.bbox) instance.fitBounds([[e.bbox[1], e.bbox[0]], [e.bbox[3], e.bbox[2]]], { padding: [30, 30], maxZoom: 16 });
        else void loadView();
      }).catch((e) => setError(describeError(e as { message?: string; code?: string })));
    }
    return () => { clearTimeout(timer); instance.remove(); map.current = null; layer.current = null; houses.current = null; };
  }, []);

  useEffect(() => {
    if (!openId) { setSummary(null); return; }
    let live = true;
    setSummary(null);
    void callRpc<Summary | null>('fn_plot_map_summary', { p_plot_id: openId })
      .then((s) => { if (live) setSummary(s); })
      .catch((e) => { if (live) setError(describeError(e as { message?: string; code?: string })); });
    return () => { live = false; };
  }, [openId]);

  const s = summary;
  return (
    <LiveScreen subtitle="Every plot boundary on satellite imagery. Click a plot for its summary." title="Earth View">
      <div className="map-legend">
        <span style={{ color: COLOURS.alert }}><i className="map-legend-swatch" />Open satellite alert</span>
        <span style={{ color: COLOURS.monitored }}><i className="map-legend-swatch" />Monitored</span>
        <span style={{ color: COLOURS.pending }}><i className="map-legend-swatch" />Awaiting baseline</span>
        <span style={{ color: COLOURS.other }}><i className="map-legend-swatch" />Mapped, not monitored</span>
        <span style={{ color: COLOURS.draft }}><i className="map-legend-swatch" />Draft</span>
        <span style={{ color: HOUSE_COLOUR }}><i className="map-legend-dot" />Farmer's house (GPS)</span>
      </div>
      <div className="toolbar-row earth-view-controls">
        <div className="field">
          <Select onChange={setFarmerId} placeholder={farmers.isLoading ? 'Loading farmers...' : 'All farmers'}
                  options={(farmers.data ?? []).map((row) => ({
                    id: row.id,
                    label: `${row.name}${row.plots ? ` (${row.plots})` : ' (house only)'}${row.code ? ` - ${row.code}` : ''}`,
                  }))}
                  value={farmerId} />
        </div>
        <div aria-label="Show on the map" className="check-list" role="group">
          <label><input checked={showPlots} onChange={(event) => setShowPlots(event.target.checked)} type="checkbox" /> Farmer plots</label>
          <label><input checked={showHouses} onChange={(event) => setShowHouses(event.target.checked)} type="checkbox" /> Farmers' houses (GPS)</label>
        </div>
      </div>
      <p className="muted">
        {extent ? `${formatNumber(extent.mapped_count)} of ${formatNumber(extent.plot_count)} plots have a boundary. ` : ''}
        {showPlots ? `${formatNumber(shown)} plot${shown === 1 ? '' : 's'} in this view` : 'Plots hidden'}
        {showHouses ? `, ${formatNumber(housesShown)} house${housesShown === 1 ? '' : 's'}.` : '.'}
        {truncated ? ' Zoom in to see them all - only the first few thousand are drawn at this scale.' : ''}
      </p>
      {error && <p className="login-error" role="alert">{error}</p>}
      <div className="org-map-canvas" ref={container} />

      {openId && (
        <RecordDrawer
          footer={<>
            <button onClick={() => setOpenId(null)} type="button">Close</button>
            {s?.plot.plot_code && <Link className="primary button-link" to={`/plots?q=${encodeURIComponent(s.plot.plot_code)}`}>Open plot</Link>}
          </>}
          onClose={() => setOpenId(null)} open
          subtitle={s ? [s.place.village, s.place.district, s.place.state].filter(Boolean).join(', ') : 'Loading'}
          title={s ? `${s.plot.plot_code ?? 'Plot'}${s.plot.plot_name ? ` - ${s.plot.plot_name}` : ''}` : 'Plot'}
        >
          {!s ? <p className="muted">Loading the summary...</p> : (
            <>
              <dl className="detail-grid">
                <div><dt>Farmer</dt><dd>{s.farmer.name ?? '--'}{s.farmer.code ? ` (${s.farmer.code})` : ''}</dd></div>
                <div><dt>Status</dt><dd><StatusPill value={s.plot.is_draft ? 'draft' : s.plot.plot_status} /></dd></div>
                <div><dt>Declared area (land record)</dt><dd>{s.plot.declared_area_ha != null ? `${formatNumber(s.plot.declared_area_ha, 2)} ha` : '--'}</dd></div>
                <div><dt>Mapped area</dt><dd>{s.plot.mapped_area_ha != null ? `${formatNumber(s.plot.mapped_area_ha, 2)} ha` : '--'}
                  {s.plot.area_variance_pct != null ? ` (${formatNumber(s.plot.area_variance_pct, 1)}% off declared)` : ''}</dd></div>
                <div><dt>Risk</dt><dd><StatusPill value={s.plot.risk_status} /></dd></div>
                <div><dt>Farm plot type</dt><dd>{s.plot.plot_type ? optionLabel(PLOT_TYPES, s.plot.plot_type) : '--'}</dd></div>
                <div><dt>FSC claim</dt><dd>{s.plot.fsc_claim ? optionLabel(FSC_CLAIMS, s.plot.fsc_claim) : '--'}</dd></div>
                <div><dt>Features</dt><dd>{s.plot.conservation_feature_count} conservation, {s.plot.hazard_feature_count} hazard
                  {s.plot.has_indigenous_neighbour ? '; indigenous neighbour' : ''}</dd></div>
              </dl>
              <h4>Trees</h4>
              {s.trees && s.trees.standing > 0 ? (
                <dl className="detail-grid">
                  <div><dt>Standing</dt><dd>{formatNumber(s.trees.standing)}</dd></div>
                  {s.trees.species.slice(0, 4).map((t) => <div key={t.species}><dt>{t.species}</dt><dd>{formatNumber(t.standing ?? 0)}</dd></div>)}
                </dl>
              ) : <p className="muted">No tree inventory recorded.</p>}
              <h4>Satellite</h4>
              {s.monitoring ? (
                <dl className="detail-grid">
                  <div><dt>Monitoring</dt><dd><StatusPill value={s.monitoring.status} /></dd></div>
                  <div><dt>Baseline</dt><dd>{s.monitoring.baseline_at ? formatDate(s.monitoring.baseline_at) : 'not yet'}</dd></div>
                  <div><dt>Last checked</dt><dd>{s.monitoring.last_checked_at ? formatDateTime(s.monitoring.last_checked_at) : '--'}</dd></div>
                  <div><dt>Next due</dt><dd>{s.monitoring.next_due_at ? formatDate(s.monitoring.next_due_at) : '--'}</dd></div>
                  <div><dt>Observations</dt><dd>{s.monitoring.observations}</dd></div>
                  <div><dt>Open changes</dt><dd>{s.monitoring.open_changes}</dd></div>
                  <div><dt>Alerts</dt><dd>{s.alerts?.open ?? 0} open of {s.alerts?.total ?? 0}</dd></div>
                </dl>
              ) : <p className="muted">Not under satellite monitoring.</p>}
            </>
          )}
        </RecordDrawer>
      )}
    </LiveScreen>
  );
}
